// @djsem/analysis-contract — Event & Release Value-Add Layer
// Prompt 43: Event Brief Builder
// Prompt 45: Technical Rider / Venue Handoff
// Prompt 46: Missing Track Finder / Playlist Gap Analysis
// Prompt 47: EPK / Press Kit Generator
// Prompt 52: Brand Consistency Scoring
// Prompt 53: Release Readiness Coach

import { SeqCounter } from "@djsem/shared";
const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();

// ============================================================================
// PROMPT 53 — Release Readiness Coach
// ============================================================================

/** The overall verdict for a release's readiness assessment. */
export type DJSEMReleaseReadinessVerdict = "ready" | "mostly_ready" | "needs_work" | "blocked";

/** A single scored dimension (metadata, artwork, campaign, rights, brand fit) of release readiness. */
export interface DJSEMReleaseReadinessDimensionScore {
  dimension: string;
  score: number;
  explanation: string;
  blockers: string[];
  warnings: string[];
}

/** A single missing item blocking or degrading release readiness, with a suggested action. */
export interface DJSEMReleaseMissingItem {
  id: string;
  itemType: string;
  severity: "info" | "warning" | "high" | "critical";
  suggestedAction: string;
}

/** A suggested next action to improve or unblock a release's readiness. */
export interface DJSEMReleaseNextAction {
  id: string;
  label: string;
  actionType: string;
  blocked: boolean;
  blockerReason?: string;
}

/** The full release readiness assessment: overall score/verdict, dimension breakdown, gaps, and next actions. */
export interface DJSEMReleaseReadinessReport {
  id: string;
  releaseId: string;
  overallScore: number;
  verdict: DJSEMReleaseReadinessVerdict;
  dimensionScores: DJSEMReleaseReadinessDimensionScore[];
  missingItems: DJSEMReleaseMissingItem[];
  nextActions: DJSEMReleaseNextAction[];
  generatedAt: string;
}

/** Flags and scores describing a release's current state, used to compute its readiness. */
export interface ReleaseReadinessInput {
  releaseId: string;
  hasTitle: boolean; hasArtist: boolean; hasCoverArt: boolean;
  hasDescription: boolean; hasCampaignPlan: boolean;
  hasEpk: boolean; hasRightsDeclaration: boolean;
  brandConsistencyScore?: number;
  rightsBlocker: boolean;
}

/** Scores a release's readiness across metadata, artwork, campaign, rights, and brand fit, with next actions. */
export class ReleaseReadinessCoachService {
  /**
   * Scores each readiness dimension from the input flags, collects missing items and blockers,
   * and derives an overall score, verdict, and recommended next actions.
   * @param input - the release's current completeness/rights/brand-fit state.
   * @returns the full release readiness report.
   */
  generateReport(input: ReleaseReadinessInput): DJSEMReleaseReadinessReport {
    const dims: DJSEMReleaseReadinessDimensionScore[] = [];
    const missing: DJSEMReleaseMissingItem[] = [];
    const actions: DJSEMReleaseNextAction[] = [];

    // Metadata
    const metaScore = [input.hasTitle, input.hasArtist, input.hasDescription].filter(Boolean).length / 3;
    dims.push({ dimension: "metadata", score: metaScore * 100, explanation: `${Math.round(metaScore * 3)}/3 metadata fields complete`, blockers: [], warnings: metaScore < 1 ? ["Incomplete metadata"] : [] });
    if (!input.hasTitle) missing.push({ id: `rm_${_seq.next()}`, itemType: "title", severity: "critical", suggestedAction: "Add release title" });
    if (!input.hasArtist) missing.push({ id: `rm_${_seq.next()}`, itemType: "artist", severity: "critical", suggestedAction: "Add artist name" });
    if (!input.hasDescription) missing.push({ id: `rm_${_seq.next()}`, itemType: "release_description", severity: "warning", suggestedAction: "Add release description" });

    // Artwork
    const artScore = input.hasCoverArt ? 100 : 0;
    dims.push({ dimension: "artwork", score: artScore, explanation: input.hasCoverArt ? "Cover art present" : "Cover art missing", blockers: input.hasCoverArt ? [] : ["Cover art required"], warnings: [] });
    if (!input.hasCoverArt) missing.push({ id: `rm_${_seq.next()}`, itemType: "cover_art", severity: "critical", suggestedAction: "Generate or upload cover artwork" });
    if (!input.hasCoverArt) actions.push({ id: `ra_${_seq.next()}`, label: "Generate artwork", actionType: "generate_artwork", blocked: false });

    // Campaign
    const campScore = input.hasCampaignPlan ? 80 : 0;
    dims.push({ dimension: "campaign_plan", score: campScore, explanation: input.hasCampaignPlan ? "Campaign plan exists" : "No campaign plan", blockers: [], warnings: input.hasCampaignPlan ? [] : ["Consider creating a campaign plan"] });

    // Rights
    const rightsScore = input.rightsBlocker ? 0 : input.hasRightsDeclaration ? 100 : 30;
    dims.push({ dimension: "rights_provenance", score: rightsScore, explanation: input.rightsBlocker ? "Rights blocker present" : input.hasRightsDeclaration ? "Rights declared" : "Rights status unknown", blockers: input.rightsBlocker ? ["Rights blocker overrides all"] : [], warnings: !input.hasRightsDeclaration && !input.rightsBlocker ? ["Review rights before release"] : [] });

    // Brand
    if (input.brandConsistencyScore !== undefined) {
      dims.push({ dimension: "brand_fit", score: input.brandConsistencyScore * 100, explanation: `Brand consistency: ${(input.brandConsistencyScore * 100).toFixed(0)}%`, blockers: [], warnings: input.brandConsistencyScore < 0.5 ? ["Off-brand assets"] : [] });
    }

    // Overall
    const total = dims.reduce((s, d) => s + d.score, 0) / dims.length;
    const hasBlocker = input.rightsBlocker || !input.hasCoverArt;
    const verdict: DJSEMReleaseReadinessVerdict = hasBlocker ? "blocked" : total >= 85 ? "ready" : total >= 70 ? "mostly_ready" : "needs_work";

    if (!input.hasRightsDeclaration) actions.push({ id: `ra_${_seq.next()}`, label: "Review rights", actionType: "review_rights", blocked: false });
    if (!input.hasEpk) actions.push({ id: `ra_${_seq.next()}`, label: "Create EPK", actionType: "create_epk", blocked: false });

    return { id: `rrr_${_seq.next()}`, releaseId: input.releaseId, overallScore: total, verdict, dimensionScores: dims, missingItems: missing, nextActions: actions, generatedAt: AT };
  }
}
